import { useState, useEffect } from 'react';

const SERVER_URL = 'http://localhost:3001';

/**
 * Hook for polling an image generation job's status
 * Uses server API polling instead of Firestore realtime to avoid permission issues
 *
 * @param {string|null} jobId - The job ID to subscribe to
 * @returns {{ job: Object|null, loading: boolean, error: string|null }}
 */
export function useImageGenerationJob(jobId) {
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!jobId) {
            setJob(null);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        let pollInterval = null;

        const fetchJob = async () => {
            try {
                const response = await fetch(`${SERVER_URL}/api/image-generation-jobs/${jobId}`);

                if (!response.ok) {
                    if (response.status === 404) {
                        if (!cancelled) {
                            setJob(null);
                            setError('Job not found');
                            setLoading(false);
                        }
                        return;
                    }
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();
                if (!cancelled) {
                    setJob(data);
                    setLoading(false);

                    // Stop polling if job is complete
                    if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
                        if (pollInterval) {
                            clearInterval(pollInterval);
                            pollInterval = null;
                        }
                    }
                }
            } catch (err) {
                console.error('Error fetching image generation job:', err);
                if (!cancelled) {
                    setError(err.message);
                    setLoading(false);
                }
            }
        };

        // Initial fetch
        setLoading(true);
        setError(null);
        fetchJob();

        // Poll every 3 seconds while job is active
        pollInterval = setInterval(fetchJob, 3000);

        return () => {
            cancelled = true;
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [jobId]);

    return { job, loading, error };
}

/**
 * Hook for checking recent jobs for a user and chat
 * Used for job recovery on page load - fetches ALL recent jobs including failed ones
 * Uses server API to avoid Firestore permission issues
 *
 * @param {string} userId - User ID
 * @param {string|null} chatId - Optional chat ID to filter by
 * @returns {{ recentJobs: Array, loading: boolean }}
 */
export function usePendingImageJobs(userId, chatId = null) {
    const [pendingJobs, setPendingJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Don't fetch for new chats (no chatId) - only fetch for existing saved chats
        if (!userId || !chatId) {
            setPendingJobs([]);
            setLoading(false);
            return;
        }

        let cancelled = false;

        // Fetch recent jobs from server API for this specific chat (includes failed/completed for recovery)
        const fetchRecentJobs = async () => {
            try {
                setLoading(true);
                const params = new URLSearchParams({ userId });
                if (chatId) params.append('chatId', chatId);
                // Don't filter by status - get all recent jobs

                const response = await fetch(`${SERVER_URL}/api/image-generation-jobs?${params}`);

                if (!response.ok) {
                    console.warn('Failed to fetch recent jobs:', response.status);
                    return;
                }

                const data = await response.json();
                if (!cancelled) {
                    // Return jobs that need attention (processing OR recently failed/completed)
                    const jobs = data.jobs || [];
                    setPendingJobs(jobs);
                }
            } catch (err) {
                console.warn('Error fetching recent image jobs:', err.message);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchRecentJobs();

        return () => {
            cancelled = true;
        };
    }, [userId, chatId]);

    return { pendingJobs, loading };
}

/**
 * Start a background image generation job
 * Returns immediately with jobId - generation continues on server
 *
 * @param {Object} params - Generation parameters
 * @param {string} params.userId - User ID
 * @param {string} params.brandId - Brand ID
 * @param {string} params.chatId - Chat ID for recovery
 * @param {Array} params.prompts - Array of prompt objects
 * @param {Array} params.referenceImages - Reference images for style
 * @param {string} params.aspectRatio - Output aspect ratio
 * @param {string} params.outputType - 'image' or 'carousel'
 * @param {Object} params.creativeBrief - Creative brief data
 * @returns {Promise<{jobId: string, status: string, message: string}>}
 */
export async function startImageGenerationJob({
    userId,
    brandId,
    chatId,
    prompts,
    referenceImages,
    aspectRatio,
    outputType,
    creativeBrief
}) {
    const response = await fetch(`${SERVER_URL}/api/image-generation-jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            userId,
            brandId,
            chatId,
            prompts,
            referenceImages,
            aspectRatio,
            outputType,
            creativeBrief
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to start image generation');
    }

    return response.json();
}

/**
 * Get job status from server (fallback if Firestore not working)
 */
export async function getImageJobStatus(jobId) {
    const response = await fetch(`${SERVER_URL}/api/image-generation-jobs/${jobId}`);

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to get job status');
    }

    return response.json();
}

/**
 * Cancel a running job
 */
export async function cancelImageJob(jobId) {
    const response = await fetch(`${SERVER_URL}/api/image-generation-jobs/${jobId}/cancel`, {
        method: 'POST'
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to cancel job');
    }

    return response.json();
}

/**
 * Get recent jobs for recovery/display
 */
export async function getRecentImageJobs(userId, chatId = null) {
    const params = new URLSearchParams({ userId });
    if (chatId) params.append('chatId', chatId);

    const response = await fetch(`${SERVER_URL}/api/image-generation-jobs?${params}`);

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Failed to get recent jobs');
    }

    return response.json();
}

export default {
    useImageGenerationJob,
    usePendingImageJobs,
    startImageGenerationJob,
    getImageJobStatus,
    cancelImageJob,
    getRecentImageJobs
};
